import React, { useCallback } from "react";

import { FilterExpression } from "../../../../Helpers/FilterExpressions/FilterExpression";
import { FilterSourceInfoType, getFilterBySourceId, MultiLineFilter } from "../../MultiLine/MultiLineFilter";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { useTypedDispatch } from "../../../../../../Common/UseTypedSelector";
import { useMultilineClient } from "../../../../Engine/Core/Filtering/UseMultilineClient";
import { PathResolver, withResolvePaths } from "../../../withResolvePaths";
import { createHocDisplayName } from "../../../TypingUtils";

import { FilterSelect, FilterSelectProps } from "./FilterSelect";

export interface FilterSelectFilter {
    id: string;
    caption: string;
    filter: FilterExpression;
    separated?: boolean;
}

interface FilterInputOwnProps extends FilterSelectProps {
    multilinePath: AbsoluteModelPath;
    id: string;
    tableId: string;
    filters: FilterSelectFilter[];
    filteredUniqKey?: string;
    resolvePath: PathResolver;
}

const FilterSelectContainerWrapper: React.FC<FilterInputOwnProps> = ownProps => {
    const { multilinePath, tableId, filters, filteredUniqKey = "default", resolvePath } = ownProps;
    const dispatch = useTypedDispatch();
    const resolvedMultilinePath = resolvePath(multilinePath);
    const { client, currentFilters } = useMultilineClient(resolvedMultilinePath, filteredUniqKey, info => ({
        client: info.client,
        currentFilters: info.multilineState.filters,
    }));

    const currentFilter = getFilterBySourceId(currentFilters, "filterSelect");
    const currentFilterInfo =
        currentFilter?.filterSourceInfo.type === FilterSourceInfoType.PredefinedFilter
            ? currentFilter?.filterSourceInfo
            : undefined;

    const activeFilter =
        currentFilterInfo != undefined ? filters.find(x => x.id === currentFilterInfo.selectItemId) : undefined;

    const onApplyFilter = useCallback(
        (filterId: undefined | string | null) => {
            const filterToApply = filters.find(x => x.id === filterId);
            if (filterToApply === undefined) {
                client.removeFilter("filterSelect");
            } else {
                const result: MultiLineFilter = {
                    expression: filterToApply.filter,
                    filterSourceId: "filterSelect",
                    filterSourceInfo: {
                        type: FilterSourceInfoType.PredefinedFilter,
                        selectItemId: filterToApply.id,
                    },
                };
                client.setFilter(result);
            }
        },
        [dispatch, filters, tableId, multilinePath, filteredUniqKey]
    );

    return (
        <FilterSelect
            {...ownProps}
            filters={filters.map(x => ({ caption: x.caption, id: x.id, separated: x.separated }))}
            filterId={activeFilter?.id}
            onApplyFilter={onApplyFilter}
        />
    );
};
FilterSelectContainerWrapper.displayName = createHocDisplayName(FilterSelect, "FilterSelectContainerWrapper");

export const FilterSelectContainer = React.memo(withResolvePaths(FilterSelectContainerWrapper));
